import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Stack,
    Typography,
    Alert,
    useTheme,
    Card,
    CardContent,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Add, ChevronLeft, CreditCard, Money } from '@mui/icons-material';

import { useSubscription } from '../../hooks/useSubscription';
import { SubscriptionCard } from '../ui/plans/SubscriptionCard';
import { SubscriptionPlan } from '@domain/subscription/models/SubscriptionPlan';
import PageLayout from '@shared/shell/Layout/PageLayout';
import { Paper as CustomPaper } from '@shared/ui';
import { StripeCheckoutButton } from '@features/subscription';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';
import { Icon } from '@shared/icons';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { SubscriptionProductSelection } from '../ui/plans/SubscriptionProductSelection';
import { useProducts } from '@features/products';


export const SubscriptionConfirmPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const planId = searchParams.get('planId');

    const {
        plans,
        currentSubscription,
        updateSubscription,
        isUpdating,
        isLoadingPlans,
        createPortalSession,
    } = useSubscription();

    const theme = useTheme();

    const { products: activeProducts } = useProducts({ active: true });
    const { paymentMethods, isLoading: isLoadingPaymentMethods } = usePaymentMethods();
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);


    const [exceedsLimitBy, setExceedsLimitBy] = useState<number>(0);

    // Find the selected plan
    useEffect(() => {
        if (plans && planId) {
            const plan = plans.find(p => p.id === planId);
            setSelectedPlan(plan || null);
        }
    }, [plans, planId]);

    useEffect(() => {
        if (activeProducts?.status === 'success') {
            const maxProductsFeature = selectedPlan?.features?.find(f => f.featureKey === 'max_products')
            const maxProductsLimit = maxProductsFeature?.limitValue === -1 ? Infinity : maxProductsFeature?.limitValue || 0;
            setExceedsLimitBy(Math.max(0, activeProducts.data.length - maxProductsLimit));
        }
    }, [selectedPlan, activeProducts]);

    const handleUpdateSubscriptionError = (error: string) => {
        console.error('Failed to update subscription:', error);
    };

    const handleUpdateSubscription = async () => {
        if (!selectedPlan || !currentSubscription) return;

        try {
            await updateSubscription({
                id: currentSubscription.id,
                updates: { planId: selectedPlan.id }
            });

            // Navigate back to company page
            navigate('/company');
        } catch (error) {
            console.error('Failed to update subscription:', error);
        }
    };

    const openPortal = async () => {
        try {
            const result = await createPortalSession();
            if (result.url) {
                window.open(result.url, '_blank');
            }
        } catch (error) {
            console.error('Failed to create portal session:', error);
        }
    };


    const contactSales = () => {
        window.location.href = `mailto:hello@juststock.ai?subject=Contact Sales Request&body=I'm interested in learning more about JustStock's enterprise offerings.`;
    };

    if (isLoadingPlans) {
        return (
            <PageLayout title="Subscription Details">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            </PageLayout>
        );
    }

    if (!selectedPlan) {
        return (
            <PageLayout title="Subscription Details">
                <Alert severity="error">
                    Subscription plan not found. Please try again.
                </Alert>
            </PageLayout>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            <Box sx={{ maxWidth: '1200px', margin: '0 auto', p: 3 }}>
                <Stack spacing={1} mb={3} direction="column">
                    <Button
                        startIcon={<ChevronLeft />}
                        onClick={() => navigate('/company')}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Back
                    </Button>
                </Stack>


                <Stack spacing={3} direction="row" alignItems="flex-start" >

                    <Stack spacing={3} direction="column" flexGrow={1}>
                        {currentSubscription?.status === 'canceled' ? (
                            <CustomPaper>
                                <Stack spacing={2} p={3} alignItems="flex-start" flexGrow={1} >
                                    <Typography variant="h6" gutterBottom>
                                        Continue using JustStock
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Subscribe to the {selectedPlan.name} plan to continue using JustStock.
                                    </Typography>


                                    <BorderedBlock >

                                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>

                                            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                                Need help setting up your subscription?
                                            </Typography>

                                            <Button
                                                variant="outlined"
                                                size="large"
                                                onClick={() => contactSales()}
                                            >
                                                Contact sales
                                            </Button>
                                        </Stack>

                                    </BorderedBlock>
                                </Stack>
                            </CustomPaper>
                        ) : selectedPlan.baseFeeInCents > 0 && (
                            <CustomPaper>
                                <Stack spacing={2} p={3} justifyContent="flex-start" alignItems="flex-start" >
                                    <Stack spacing={1} >
                                        <Typography variant="h6" gutterBottom>
                                            Payment methods
                                        </Typography>

                                        <Typography variant="body2" gutterBottom>
                                            Your default payment method will be used to charge your subscription.
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" justifyContent="flex-start" gap={2} flexWrap="wrap">

                                        {isLoadingPaymentMethods ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        ) : paymentMethods.length > 0 ? paymentMethods.map((paymentMethod) => (
                                            <React.Fragment key={paymentMethod.id}>
                                                <Card
                                                    sx={{
                                                        boxShadow: 'none',
                                                        border: 1,
                                                        borderColor: paymentMethod.isDefault ? 'primary.main' : 'divider',
                                                        backgroundColor: paymentMethod.isDefault ? 'primary.50' : 'background.paper',
                                                    }}
                                                >
                                                    <CardContent sx={{ display: 'flex', direction: 'row', alignItems: 'flex-start', gap: 2 }}>

                                                        {paymentMethod.type === 'sepa_debit' ? (
                                                            <>
                                                                <Money sx={{ fontSize: 24 }} />

                                                                <Box>
                                                                    <Typography variant="subtitle2" noWrap>
                                                                        SEPA Debit
                                                                    </Typography>
                                                                    {paymentMethod.isDefault && (
                                                                        <Chip
                                                                            label="Default"
                                                                            color="primary"
                                                                            size="small"
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </>

                                                        ) : paymentMethod.type === 'card' && (
                                                            <>
                                                                <CreditCard sx={{ fontSize: 24 }} />


                                                                <Box>
                                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                                        <Typography variant="subtitle2" noWrap>
                                                                            {paymentMethod.card?.brand.toUpperCase()} •••• {paymentMethod.card?.last4}
                                                                        </Typography>
                                                                        {paymentMethod.isDefault && (
                                                                            <Chip
                                                                                label="Default"
                                                                                color="primary"
                                                                                size="small"
                                                                            />
                                                                        )}
                                                                    </Stack>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Expires {paymentMethod.card?.expMonth.toString().padStart(2, '0')}/{paymentMethod.card?.expYear}
                                                                    </Typography>
                                                                </Box>
                                                            </>
                                                        )}

                                                    </CardContent>
                                                </Card>
                                            </React.Fragment>
                                        )) : (
                                            <Button onClick={() => openPortal()} variant="outlined" startIcon={<Add />} color="secondary" size="large">
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    <Typography variant="body2" color="text.primary">
                                                        No payment methods found.
                                                    </Typography>
                                                    <Typography variant="body2" color="text.primary">
                                                        Add a payment method
                                                    </Typography>
                                                </Box>
                                            </Button>
                                        )}


                                    </Stack>

                                    <Button
                                        onClick={() => openPortal()}
                                        variant="text"
                                        startIcon={<Icon icon="IcEdit" />}
                                        color="secondary"
                                        size="large"
                                    >
                                        Manage payment methods
                                    </Button>
                                </Stack>
                            </CustomPaper>
                        )}

                        {/* Product Management */}
                        {activeProducts?.status === 'success' && (
                            <SubscriptionProductSelection selectedPlan={selectedPlan} exceedsLimitBy={exceedsLimitBy} productsCount={activeProducts.data.length} activeProducts={activeProducts.data} />
                        )}
                    </Stack>

                    <Stack minWidth={300} spacing={1} direction="column"
                        sx={{
                            position: 'sticky',
                            top: 20,
                            height: 'fit-content',
                            alignSelf: 'flex-start',
                            zIndex: 1000,
                        }}
                    >
                        <SubscriptionCard
                            plan={selectedPlan}
                        />


                        {currentSubscription?.status === 'canceled' ? (
                            <StripeCheckoutButton
                                amount={selectedPlan.baseFeeInCents}
                                planName={selectedPlan.name}
                                planId={selectedPlan.id}
                                onError={handleUpdateSubscriptionError}
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={isUpdating || exceedsLimitBy > 0}
                            />
                        ) : (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleUpdateSubscription}
                                disabled={isUpdating || (paymentMethods.length === 0 && selectedPlan.baseFeeInCents > 0) || exceedsLimitBy > 0}
                                startIcon={isUpdating ? <CircularProgress size={16} /> : undefined}
                            >
                                {isUpdating ? 'Updating...' : 'Update Subscription'}
                            </Button>
                        )}
                    </Stack>
                </Stack>

            </Box>

        </Box >
    );
};
